import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import campaignsApi from 'api/campaigns'
import { parseDateYYYYMMDD } from 'utils/helpers'
import { useImmerReducerAsync } from 'hooks/useReducerAsync'
import { getEventMarketingCampaignState } from 'api/models'
import './CampaignConfigProvider.types.js'

/**
 * @type {React.Context<CampaignConfigStateType>}
 */
export const CampaignConfigContext = createContext()

/**
 * @type {React.Context<CampaignConfigDispatchType>}
 */
export const CampaignConfigDispatchContext = createContext()

export const actions = Object.freeze({
  fetchCampaignAsync: 'FETCH_CAMPAIGN_ASYNC',
  fetchTaskAsync: 'FETCH_TASK_ASYNC',
  setEditMode: 'SET_EDIT_MODE',
  setForm: 'SET_FORM',
  setLoading: 'SET_LOADING',
  setError: 'SET_ERROR',
  setCampaignAndTask: 'SET_CAMPAIGN_AND_TASK',
  setTask: 'SET_TASK',
  setDailyBudgetPreview: 'SET_DAILY_BUDGET_PREVIEW',
  saveAsync: 'SAVE_ASYNC',
})

/**
 * @type {CampaignConfigStateType}
 */
export const initialState = {
  loading: false,
  error: null, // { message, code, status } or null
  campaign: {},
  task: null,
  form: {
    total_budget: 0,
    start_date: null,
    end_date: null,
  },
  editMode: false,
  dailyBudgetPreview: null,
}

export const reducer = (draft, action) => {
  switch (action.type) {
    case actions.setLoading:
      draft.loading = action.payload
      break
    case actions.setError: {
      // Accepts error object or string, handles axios errors
      let err = action.payload
      let errorSource = err && err.errorSource ? err.errorSource : undefined
      if (typeof err === 'string') {
        err = { message: err, code: null, status: null }
      }
      // Axios error: extract from response
      if (err && err.response) {
        const { status, data } = err.response
        err = {
          ...err,
          status: status || err.status,
          code: (data && (data.code || data.error_code)) || err.code,
          message: (data && (data.message || data.error_message)) || err.message,
          data: data?.data,
          raw: err,
        }
      }
      draft.error = {
        message: err.message || err.error_message || 'Unknown error',
        code: err.code || err.error_code || null,
        status: err.status || err.statusCode || null,
        raw: err,
        ...(err.data ? { data: err.data } : {}),
        ...(errorSource ? { errorSource } : {}),
      }
      draft.loading = false
      break
    }
    case actions.setCampaignAndTask: {
      const { campaign, task } = action.payload
      // Convert date strings to Date objects if present
      const campaignStartDate =
        typeof campaign.start_date === 'string'
          ? parseDateYYYYMMDD(campaign.start_date)
          : campaign.start_date || null
      const campaignEndDate =
        typeof campaign.end_date === 'string'
          ? parseDateYYYYMMDD(campaign.end_date)
          : campaign.end_date || null
      const campaignStatus = campaign.status || 'found'
      draft.campaign = { ...campaign, start_date: campaignStartDate, end_date: campaignEndDate, status: campaignStatus }
      draft.form = {
        ...draft.form,
        total_budget: campaign.total_budget,
        // start_date remains as is (empty unless user changes)
        end_date: campaignEndDate,
      }
      if (task?.args) {
        const taskStartDate =
          typeof task.args.start_date === 'string'
            ? parseDateYYYYMMDD(task.args.start_date)
            : task.args.start_date || null
        const taskEndDate =
          typeof task.args.end_date === 'string'
            ? parseDateYYYYMMDD(task.args.end_date)
            : task.args.end_date || null
        draft.task = {
          ...task,
          args: { start_date: taskStartDate, end_date: taskEndDate, ...task.args },
        }
      }
      draft.loading = false
      draft.error = null
      break
    }
    case actions.setTask: {
      draft.task = action.payload
      draft.loading = false
      break
    }
    case actions.setForm: {
      // If start_date or end_date are being set, convert to Date objects
      const nextForm = { ...draft.form, ...action.payload }
      if (nextForm.start_date && typeof nextForm.start_date === 'string') {
        nextForm.start_date = parseDateYYYYMMDD(nextForm.start_date)
      }
      if (nextForm.end_date && typeof nextForm.end_date === 'string') {
        nextForm.end_date = parseDateYYYYMMDD(nextForm.end_date)
      }
      draft.form = nextForm
      break
    }
    case actions.setEditMode:
      draft.editMode = action.payload
      break
    case actions.setDailyBudgetPreview:
      draft.dailyBudgetPreview = action.payload
      draft.loading = false
      break
    default:
      // do nothing
      break
  }
}

function useConfigPolling(shouldPoll, intervalMs, callback) {
  const intervalRef = useRef(null)
  const savedCallback = useRef(callback)

  // keep latest callback without resetting interval
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    if (!shouldPoll) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    if (intervalRef.current) return

    intervalRef.current = setInterval(() => {
      savedCallback.current()
    }, intervalMs)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [shouldPoll, intervalMs])
}

export const asyncActionHandlers = {
  [actions.fetchCampaignAsync]:
    ({ dispatch }) =>
      async (action) => {
        dispatch({ type: actions.setLoading, payload: true })
        const { eventId } = action.payload
        const { data, error } = await campaignsApi.fetchCurrentCampaignRuntimeAndBudget(eventId)
        if (error) {
          if (
            error.response.data.error_code === 'error.campaign.config.remote_campaign_not_published'
          ) {
            dispatch({
              type: actions.setCampaignAndTask,
              payload: {
                campaign: {
                  start_date: null,
                  end_date: null,
                  total_budget: null,
                  currency: null,
                  status: 'not_published',
                },
                task: null,
              },
            })
          } else {
            dispatch({ type: actions.setError, payload: error })
          }
          return
        }
        const campaign = {
          start_date: data.start_date,
          end_date: data.end_date,
          total_budget: data.total_budget,
          min_daily_budget: data.min_daily_budget,
          min_budget: data.min_budget,
          max_budget: data.max_budget,
          currency: data.currency,
          has_permission_error: data.has_permission_error,
        }
        const task = data.task_details
          ? {
            ...(data.task_details || {}),
            timestamp: data.task_details?.datetime ? new Date(data.task_details.datetime) : null,
            mappedStatus: mapTaskStatus(data.task_details),
            args: data.task_details?.args
              ? {
                budget: data.task_details.args.budget,
                start_date: data.task_details.args.start_date
                  ? parseDateYYYYMMDD(data.task_details.args.start_date)
                  : null,
                end_date: data.task_details.args.end_date
                  ? parseDateYYYYMMDD(data.task_details.args.end_date)
                  : null,
              }
              : null,
          }
          : null
        dispatch({ type: actions.setCampaignAndTask, payload: { campaign, task } })
        if (campaign.has_permission_error) {
          dispatch({ type: actions.setError, payload: { message: 'Permission error' } })
        }
      },
}

export default function CampaignConfigProvider({ children, event }) {
  const { hasMarketingCampaign } = getEventMarketingCampaignState(event)
  const [state, dispatch] = useImmerReducerAsync(reducer, {
    ...initialState,
    campaign: {
      ...initialState.campaign,
      status: hasMarketingCampaign ? 'found' : 'not_found',
    }
  }, asyncActionHandlers)

  const fetchCurrentCampaignConfig = useCallback(() => {
    dispatch({ type: actions.fetchCampaignAsync, payload: { eventId: event.id } })
  }, [dispatch, event.id])

  const _hasPendingPublishTask = state.campaign.status === 'not_published'
  const _hasPendingTask = state.task?.mappedStatus?.toLowerCase() === 'pending'
  useConfigPolling(
    _hasPendingPublishTask || _hasPendingTask,
    _hasPendingPublishTask ? 30000 : 180000,
    fetchCurrentCampaignConfig,
  )

  useEffect(() => {
    if (hasMarketingCampaign && event?.id) {
      fetchCurrentCampaignConfig()
    }
    // eslint-disable-next-line
  }, [hasMarketingCampaign, event?.id])

  const hasPendingPublishTask = useCallback(() => {
    return state.campaign.status === 'not_published'
  }, [state.campaign.status])

  const getDailyBudgetPreview = useCallback(
    async (cb) => {
      dispatch({ type: actions.setLoading, payload: true })
      const { total_budget, start_date, end_date } = state.form
      try {
        const { data, error } = await campaignsApi.getDailyBudgetPreview(
          event.id,
          total_budget,
          start_date,
          end_date,
        )
        if (error) {
          dispatch({ type: actions.setError, payload: { ...error, errorSource: 'preview' } })
          if (cb) cb({ success: false, error })
          return
        }
        dispatch({ type: actions.setDailyBudgetPreview, payload: data })
        if (cb) cb({ success: true, data })
      } catch (err) {
        console.error('Error fetching daily budget preview:', err)
        dispatch({ type: actions.setError, payload: { ...err, errorSource: 'preview' } })
        if (cb) cb({ success: false, error: err })
      }
    },
    [dispatch, state.form, event.id],
  )

  const postBudgetAndRuntime = useCallback(
    async (cb) => {
      dispatch({ type: actions.setLoading, payload: true })
      const { total_budget, start_date, end_date } = state.form
      const { start_date: campaignStartDate } = state.campaign
      try {
        const { data, error } = await campaignsApi.postBudgetAndRuntime(
          event.id,
          total_budget,
          start_date,
          end_date,
        )
        if (error) {
          dispatch({ type: actions.setError, payload: { ...error, errorSource: 'save' } })
          if (cb) cb({ success: false, error })
          return
        }

        const mappedTask = { ...data, mappedStatus: mapTaskStatus(data) }
        const campaign = {
          total_budget,
          end_date,
          start_date: start_date ?? campaignStartDate,
        }
        const task = {
          ...mappedTask,
          args: {
            budget: total_budget,
            start_date,
            end_date,
          },
        }
        dispatch({ type: actions.setCampaignAndTask, payload: { campaign, task } })

        if (cb) cb({ success: true, data: mappedTask })
      } catch (err) {
        dispatch({ type: actions.setError, payload: { ...err, errorSource: 'save' } })
        if (cb) cb({ success: false, error: err })
      }
    },
    [dispatch, state.form, state.campaign, event.id],
  )

  const toggleEditMode = () => {
    dispatch({ type: actions.setEditMode, payload: !state.editMode })
  }

  const resetCampaignForm = () => {
    dispatch({
      type: actions.setForm,
      payload: {
        start_date: state.campaign.start_date,
        end_date: state.campaign.end_date,
        total_budget: state.campaign.total_budget,
      },
    })
  }

  const resetDailyBudgetPreview = () => {
    dispatch({ type: actions.setDailyBudgetPreview, payload: null })
  }

  const setCampaignForm = (data) => {
    dispatch({
      type: actions.setForm,
      payload: data,
    })
  }

  const hasPermissionError = () => {
    if (state.campaign.has_permission_error) return true
    if (!state.error) return false
    return (
      state.error.status === 403 ||
      [
        'error.connect.facebook.invalid_token',
        'error.connect.facebook.missing_permissions',
      ].includes(state.error.error_code)
    )
  }

  const hasPendingTask = () => {
    if (!state.task) return false
    return state.task?.mappedStatus?.toLowerCase() === 'pending'
  }

  return (
    <CampaignConfigContext.Provider value={state}>
      <CampaignConfigDispatchContext.Provider
        value={{
          dispatch,
          fetchCurrentCampaignConfig,
          toggleEditMode,
          resetCampaignForm,
          setCampaignForm,
          getDailyBudgetPreview,
          resetDailyBudgetPreview,
          postBudgetAndRuntime,
          hasPermissionError,
          hasPendingTask,
          hasPendingPublishTask,
        }}
      >
        {children}
      </CampaignConfigDispatchContext.Provider>
    </CampaignConfigContext.Provider>
  )
}

CampaignConfigProvider.propTypes = {
  children: PropTypes.node.isRequired,
  event: PropTypes.object.isRequired,
}

/**
 * @returns {CampaignConfigStateType}
 */
export function useCampaignConfig() {
  const context = useContext(CampaignConfigContext)
  if (!context) {
    throw new Error('useCampaignConfig must be used within a CampaignConfigProvider')
  }
  return context
}

/**
 * @returns {CampaignConfigDispatchType}
 */
export function useCampaignConfigDispatch() {
  const context = useContext(CampaignConfigDispatchContext)
  if (!context) {
    throw new Error('useCampaignConfigDispatch must be used within a CampaignConfigProvider')
  }
  return context
}

// Map celery task status to domain status for UI
export function mapTaskStatus(task) {
  if (!task || !task.status) return null
  const status = task.status.toLowerCase()
  if (
    [
      'pending',
      'started',
      'in_progress',
      'progress',
      'running',
      'queued',
      'received',
      'scheduled',
      'retry',
    ].includes(status)
  ) {
    return 'pending'
  }
  if (['success'].includes(status)) {
    return 'success'
  }
  if (['failure', 'failed', 'revoked'].includes(status)) {
    return 'failure'
  }
  return status
}
